import React, { useEffect, useState } from "react";
import closeIcon from "../../../assets/closeModel.png";
import checkedIcon from "../../../assets/check-mark.png";
import deleteIcon from "../../../assets/trash.png";
import { DialogContent, DialogContentText, Grid } from "@mui/material";
import Popup from "../../../components/common/model/Popup";
import useStyles from "../../../components/common/model/styles";
import { DEPOSIT_INVOICE_FORM_ELEMENTS } from "./invoiceUntils";
import FormField from "../../../components/common/FormField";
import { useSelector } from "react-redux";
import ResetFormAlert from "../../../components/common/ResetFormAlert";
import CustomButton from "../../../components/common/button/CustomButton";
import Validation from "../../../utils/Validation";
import {
  addDepositInvoiceAction,
  downloadInvoiceAction,
  getSingleInvoiceAction,
  initialDepositInvoiceLocalState,
  setAddInvoiceResponse,
  setDepositInvoiceCreated,
  setDownloadInvoiceAction,
  setSingleInvoiceAction,
} from "../../../store/reducers/invoiceSlice";
import { useDispatch } from "react-redux";
import Notification from "../../../components/common/notification/Notification";
import { setNotificationAction } from "../../../store/reducers/mmymSlice";
import {
  setAddPaymentResponse,
  setAllPaymentListAction,
} from "../../../store/reducers/paymentSlice";
import InvoiceAlertAction from "./InvoiceAlertAction";
import AlertPopup from "../../../components/common/alertPopup/AlertPopup";
import {
  cancelInvoiceDepositAction,
  setResponseCancelInvoiceDeposit,
  setResponseTransferInvoice,
} from "../../../store/reducers/clientConfirmationSlice";

interface ManageDepositInvoiceProps {
  open: boolean; // Define the 'open' prop
  onHide: () => void; // Define the 'onHide' prop
  title: string; // Define the 'title' prop
  selectedLead?: any; // Adjust type as per your requirement
  selectedInvoice?: any; // Adjust type as per your requirement
  invoiceType?: any;
  selectedQuote?: any; // Optional prop to indicate selected quote
  isFormReadyOnly?: any;
  isNewInvoice?: boolean; // Optional prop to indicate if it's a new invoice
  isSetLeadDetails?: boolean; // Optional prop to indicate if lead details should be set
}

const ManageDepositInvoice: React.FC<ManageDepositInvoiceProps> = (props) => {
  const {
    onHide,
    open,
    title,
    isNewInvoice,
    invoiceType,
    selectedInvoice,
    selectedQuote,
  } = props;
  const classes = useStyles();
  const {
    invoiceDetails,
    isFileDownloaded,
    isAddInvoiceResponse,
    isDepositInvoiceCreated,
  } = useSelector((state: any) => state.invoice);
  const { leadDetails } = useSelector((state: any) => state.lead);
  const { userProfile } = useSelector((state: any) => state?.user);
  const { isDepositResponse } = useSelector(
    (state: any) => state?.confirmation,
  );
  const [formData, setFormData] = useState<any>(
    initialDepositInvoiceLocalState,
  );
  const [isActionCalled, setIsActionCalled] = useState(false);
  const [alerModeShow, setAlertModelShow] = useState(false);
  const [isClickReset, setIsClickReset] = useState(false);
  const [actionType, setActionType] = useState("");
  const [acceptQuoteWarning, setAcceptQuoteWarning] = useState(false);
  const [transferDepositModal, setTransferDepositModal] = useState(false);
  const [cancelDepositAlert, setCancelDepositAlert] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingEmailAfterSave, setPendingEmailAfterSave] = useState(false);
  const [sendEmailAfterSave, setSendEmailAfterSave] = useState(false);
  const [isProfessionalSection, setIsProfessionalSection] = useState(
    userProfile?.features?.includes("Professional Financial") || false, // Safe fallback
  );

  const dispatch = useDispatch();
  useEffect(() => {
    setIsProfessionalSection(
      userProfile?.features?.includes("Professional Financial") || false,
    );
  }, [userProfile]);

  useEffect(()=>{
    if(open){
      dispatch(setNotificationAction({ message: [], severity: "error" }));
    }
  },[open])

  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
  ]: any = Validation({
    total_amount: formData?.total_amount,
    due_date: formData?.due_date,
    method: formData?.method,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    checkValidator();
    setIsActionCalled(true);
  };

  const hideHandler = () => {
    onHide();
    dispatch(setAddInvoiceResponse(false));
    dispatch(setSingleInvoiceAction(null));
    dispatch(setNotificationAction({ message: [], severity: "error" }));
    dispatch(
      setAllPaymentListAction({ pagination: { currentPage: 1 }, list: [] }),
    );
    dispatch(setAddPaymentResponse(true));
    resetAllValidator();
    setFormData(null);
  };

  useEffect(() => {
    if (leadDetails) {
      const payload: any = {};
      if (selectedInvoice) {
        payload.invoiceNo = selectedInvoice;
      } else {
        payload.leadNo = leadDetails?.lead?.generated_id;
        payload.invoiceType = invoiceType;
        payload.quoteId = selectedQuote?.quoteId ?? null;
      }
      dispatch(getSingleInvoiceAction(payload));
      dispatch(setAddInvoiceResponse(false));
      dispatch(setDepositInvoiceCreated(false));
    }
  }, [
    selectedInvoice,
    leadDetails,
    isAddInvoiceResponse,
    isDepositInvoiceCreated,
  ]);

  useEffect(() => {
    if (!invoiceDetails) return;

    // 🏗️ Format address string for display (optional for UI)
    const formattedAddress = [
      invoiceDetails.address?.building_number,
      invoiceDetails.address?.street,
      invoiceDetails.address?.town,
      invoiceDetails.address?.county,
      invoiceDetails.address?.postcode,
      invoiceDetails.address?.country,
    ]
      .filter(Boolean)
      .join(", ");

    // 📅 Helper to calculate due date
    const calculateDueDate = (days: string | number | undefined) => {
      const parsedDays = parseInt(days as string, 10);
      if (isNaN(parsedDays)) return null;
      const newDate = new Date();
      newDate.setDate(newDate.getDate() + parsedDays);
      return newDate.toISOString().split("T")[0]; // format: YYYY-MM-DD
    };

    // 💰 Helper: calculate deposit total
    const calculateDepositAmount = (
      quoteAmount: number | string | null | undefined,
      depositPercent: number | string | null | undefined,
    ) => {
      const amount = parseFloat(quoteAmount as string);
      const percent = parseFloat(depositPercent as string);
      if (isNaN(amount) || isNaN(percent)) return null;
      return (amount * percent) / 100;
    };

    if (invoiceDetails?.invoice_number) {
      // ✅ Case 1: Existing invoice — use existing due_date
      setFormData({
        invoice_type: invoiceDetails.invoice_type ?? "deposit",
        invoice_number: invoiceDetails.invoice_number ?? null,
        customer_name: invoiceDetails.customer_name ?? null,
        address: {
          id: invoiceDetails.address?.id ?? null,
          fullAddress: formattedAddress || "",
        },
        quote_primary_task: invoiceDetails.quote_primary_task ?? null,
        quote_amount: invoiceDetails.quote_amount ?? null,
        total_amount: invoiceDetails.total_amount ?? null,
        due_date: invoiceDetails.due_date ?? null,
        status: invoiceDetails.status === "Paid",
        method: invoiceDetails.method ?? null,
        lead_id: invoiceDetails.lead_id ?? null,
        quote_id: invoiceDetails.quote_id ?? null,
        customer_id: invoiceDetails.customer_id ?? null,
      });
    } else {
      // ✅ Case 2: New invoice — calculate due date using invoice_due_days
      const calculatedDueDate = calculateDueDate(
        invoiceDetails.invoice_due_days,
      );
      // 🔍 Extract "Removal" service description
      const removalService = invoiceDetails.services?.find(
        (service: any) => service.typeName?.toLowerCase() === "removal" || service?.type?.toLowerCase() === "removal",
      );

      const removalDescription = removalService?.description ?? null;

      // 💰 Calculate total amount = (quote_amount * deposit_payment_percent) / 100
      const calculatedTotal = calculateDepositAmount(
        invoiceDetails.quote_amount ?? 0,
        invoiceDetails.deposit_payment_percent ?? 0,
      );

      setFormData({
        invoice_type: invoiceDetails.invoice_type ?? "deposit",
        invoice_number: null,
        customer_name: invoiceDetails.customer_name ?? null,
        address: {
          id: invoiceDetails.address?.id ?? null,
          fullAddress: formattedAddress || "",
        },
        quote_primary_task: removalDescription ?? null,
        quote_amount: parseFloat(invoiceDetails.quote_amount ?? "0"),
        total_amount: calculatedTotal ?? null,
        due_date: isProfessionalSection
          ? calculatedDueDate
          : calculateDueDate(30),
        status: invoiceDetails.status === "Paid",
        method: invoiceDetails.method ?? null,
        lead_id: invoiceDetails.lead_id ?? null,
        quote_id: invoiceDetails.quote_id ?? selectedQuote?.quoteId ?? null,
        customer_id: invoiceDetails.customer_id ?? null,
      });
    }
    setNotificationAction({ message: [], severity: "error" });
    setIsClickReset(false);
    setAlertModelShow(false);
    setIsDirty(false);
  }, [invoiceDetails, isClickReset]);

  useEffect(() => {
    if (isDepositInvoiceCreated && pendingEmailAfterSave) {
      setPendingEmailAfterSave(false);
      setSendEmailAfterSave(true);
    }
  }, [isDepositInvoiceCreated, pendingEmailAfterSave]);

  useEffect(() => {
    if (sendEmailAfterSave && invoiceDetails?.invoice_number) {
      setSendEmailAfterSave(false);
      onShow("email");
    }
  }, [sendEmailAfterSave, invoiceDetails?.invoice_number]);

  const onChangeAction = (event: any) => {
    const { id, value } = event.target;

    let updateVal = null;
    if (id === "total_amount" || id === "quote_amount") {
      updateVal = value === "" ? null : value;
    } else {
      updateVal = value;
    }
    const updatedData = {
      ...formData,
      [id]: updateVal,
    };
    setFormData(updatedData);
    setIsDirty(true);
  };

  const handleEmailClick = () => {
    const shouldSaveBeforeEmail = !formData?.invoice_number || isDirty;
    if (shouldSaveBeforeEmail) {
      setPendingEmailAfterSave(true);
      checkValidator();
      setIsActionCalled(true);
      return;
    }
    onShow("email");
  };

  const onBlurAction = (e: any) => {
    checkValidator(e.target.id);
  };

  const openClearPopup = () => {
    setAlertModelShow(true);
  };

  useEffect(() => {
    if (isActionCalled) {
      if (!isError() && error !== null && isActionCalled) {
        // 🧹 Prepare cleaned payload
        const cleanedData = {
          ...formData,
          // Convert boolean status to string
          status:
            formData.status === true
              ? "Paid"
              : formData.status === false
                ? "Outstanding"
                : undefined,

          // Convert total_amount to number
          total_amount: formData.total_amount
            ? Number(formData.total_amount)
            : 0,
          quote_amount: formData.quote_amount
            ? Number(formData.quote_amount)
            : 0,
        };

        // 🧽 Remove unwanted keys (customer_name and fullAddress)
        if (cleanedData.customer_name) delete cleanedData.customer_name;
        if (cleanedData.address?.fullAddress)
          delete cleanedData.address.fullAddress;

        // ✅ Remove undefined/null keys cleanly
        const sanitizedData = JSON.parse(
          JSON.stringify(cleanedData, (key, value) =>
            value === undefined || value === null ? undefined : value,
          ),
        );

        dispatch(addDepositInvoiceAction(sanitizedData));
      } else {
      }
    }
    setIsActionCalled(false);
  }, [isActionCalled]);

  const onShow = (type: any) => {
    // setOpen(true);
    setActionType(type);
  };

  useEffect(() => {
    if (actionType) {
      handleDownloadInvoice();
      setActionType("");
    }
  }, [actionType]);

  const handleDownloadInvoice = () => {
    dispatch(
      downloadInvoiceAction({
        invoice_number: selectedInvoice
          ? selectedInvoice
          : formData.invoice_number,
        actionType: actionType === "pdf" ? "download" : "email",
      }),
    );
  };

  useEffect(() => {
    if (isFileDownloaded) {
      dispatch(setDownloadInvoiceAction(false));
      // setOpen(false);
      dispatch(setNotificationAction({ message: [], severity: "error" }));
    }
  }, [isFileDownloaded]);

  useEffect(() => {
    if (invoiceDetails?.is_quote_accepted === false) {
      setAcceptQuoteWarning(true);
    } else {
      setAcceptQuoteWarning(false);
    }
  }, [invoiceDetails?.is_quote_accepted]);


  const handleCancleDeposit = () => {
    dispatch(
      cancelInvoiceDepositAction({
        invoiceId: selectedInvoice ? selectedInvoice : formData.invoice_number,
      }),
    );
  };

  const handleCloseAlert = () => {
    setCancelDepositAlert(false);
    setTransferDepositModal(false);
  };

  useEffect(() => {
    if (isDepositResponse) {
      handleCloseAlert();
      onHide();
      dispatch(setResponseCancelInvoiceDeposit(false));
      dispatch(setResponseTransferInvoice(false));
    }
  }, [isDepositResponse]);

  return (
    <>
      <Popup
        open={open} // Example value, replace with your actual state or variable
        title={title} // Example title, replace with your actual title
        onHide={hideHandler}
        fullWidth={true}
        maxWidth={"md"}
        aria-describedby="alert-dialog-slide-description"
        popupBg="#FFFFFF"
        // bgHeader="#d4f9dc"
        header={
          <>
            {invoiceDetails?.status !== "Paid" && (
              <>
                {" "}
                <img
                  onClick={(e: any) => handleSubmit(e)}
                  src={checkedIcon}
                  className={classes.checkicons}
                  // style={{ right: 40 }}
                  alt="save"
                />
                <img
                  onClick={openClearPopup}
                  src={deleteIcon}
                  className={classes.deleteicons}
                  alt="delete"
                />
              </>
            )}
            <img
              onClick={hideHandler}
              src={closeIcon}
              className={classes.closeicons}
              alt="close"
            />
          </>
        }
        footer={
          <>
            {!selectedQuote?.isQuoteAccepted &&
              selectedQuote?.invoiceNumber && (
                <>
                  <CustomButton
                    variant="contained"
                    title="Transfer Deposit to Accepted Quote"
                    onClick={() => onShow("email")}
                  />
                  <CustomButton
                    variant="contained"
                    title="Cancellation"
                    onClick={() => setCancelDepositAlert(true)}
                  />
                </>
              )}
            <CustomButton
              variant="contained"
              title="Email"
              onClick={handleEmailClick}
            />
          </>
        }
      >
        <DialogContent sx={{ padding: "10px" }}>
          <DialogContentText>
            <Notification />
            <Grid
              container
              spacing={0.5}
              sx={{
                backgroundColor: "#E5F4F1",
                border: "1px solid #CFCFCF",
                borderRadius: "5px",
                padding: "10px 6px",
                marginBottom: "4px",
              }}
            >
              {DEPOSIT_INVOICE_FORM_ELEMENTS?.map((field, i) => (
                <>
                  <FormField
                    key={i}
                    onPaste={(e: any) => e.preventDefault()}
                    onKeyDown={(event: any) => {
                      if (
                        event.keyCode === 8 ||
                        event.keyCode === 9 ||
                        event.keyCode === 37 ||
                        event.keyCode === 38 ||
                        event.keyCode === 39 ||
                        event.keyCode === 40 ||
                        event.keyCode === 18 ||
                        event.keyCode === 17 ||
                        event.ctrlKey
                      )
                        return;

                      if (
                        field?.patternAllowed &&
                        !field?.patternAllowed.test(event.key)
                      ) {
                        event.preventDefault();
                      } else if (
                        field?.patternAllowed &&
                        !field?.patternAllowed.test(
                          event.target.value + event.key,
                        )
                      ) {
                        event.preventDefault();
                      }
                    }}
                    {...{
                      ...field,
                      value:
                        field?.id === "address"
                          ? formData?.address?.fullAddress || ""
                          : formData?.[field?.id] !== undefined
                            ? typeof formData[field.id] === "number"
                              ? formData[field.id].toFixed(2)
                              : formData[field.id]
                            : "",
                      variant: "outlined",
                      onChange: onChangeAction,
                      onBlur: onBlurAction,
                      ...{
                        ...(field?.type === "checkbox" && {
                          checked: formData && formData[field?.id],
                        }),
                      },
                      isNonMarginTop: true,
                      inSingleGridForm: true,
                      disabled:
                        field?.id === "due_date"
                          ? isProfessionalSection
                            ? false
                            : true
                          : field?.id === "is_vat_included"
                            ? isNewInvoice
                              ? false
                              : true
                            : (formData?.invoice_type === "deposit" &&
                                leadDetails?.lead?.status === "JOB") ||
                              invoiceDetails?.status === "Paid",
                      InputProps: {
                        ...(field?.inputProps && field?.inputProps),
                        ...{
                          disabled:
                            field?.id === "address"
                              ? true
                              : field?.editReadOnly ||
                                (formData?.invoice_type === "deposit" &&
                                  leadDetails?.lead?.status === "JOB") ||
                                invoiceDetails?.status === "Paid",
                        },
                        sx:
                          field?.type === "text" || field?.type === "password"
                            ? { height: "36px" }
                            : { padding: "10px 4px", height: "36px" },
                      },
                      error: error && error[field?.id],
                      //   helperText: error && error[field?.id],
                    }}
                  />
                </>
              ))}
            </Grid>
          </DialogContentText>
        </DialogContent>
      </Popup>
      {cancelDepositAlert && (
        <>
          <AlertPopup
            open={cancelDepositAlert}
            onHide={handleCloseAlert}
            title={"Alert"}
            content={"Are you sure you want to cancel this deposit ?"}
            submitHandler={handleCancleDeposit}
          />
        </>
      )}
      {alerModeShow && (
        <ResetFormAlert
          open={alerModeShow}
          title=""
          alertModalShow={alerModeShow}
          setAlertModelShow={setAlertModelShow}
          setIsClickReset={setIsClickReset}
        />
      )}
      {acceptQuoteWarning && (
        <InvoiceAlertAction
          open={true}
          alertModalShow={acceptQuoteWarning}
          setAlertModelShow={setAcceptQuoteWarning}
          title="Accepted Quote Warning"
          onHide={() => {
            setAcceptQuoteWarning(false);
            hideHandler();
          }}
          onSubmit={() => setAcceptQuoteWarning(false)}
          message={
            "As there are no accepted quotes, this deposit will be made against the customer only. Do you want to generate a deposit and enter the amount manually? "
          }
        />
      )}
    </>
  );
};

export default ManageDepositInvoice;
